import { http } from "./http";
import type { SessionDto } from "./authApi";

/**
 * Cross-manager Customers list returned by GET /api/admin/customers.
 * Mirrors the shape the legacy portal showed in its admin overview
 * but trimmed to the columns the SPA actually renders.
 */
export interface AdminCustomerListItem {
  customerId: number;
  customerName: string;
  managerId: number;
  managerName: string;
  numbers: number;
  logins: number;
  billable: boolean;
  debtorName: string;
  enabled: boolean;
}

export interface AdminCustomerListResponse {
  items: AdminCustomerListItem[];
}

/** Editable customer detail (feature-flags are intentionally omitted). */
export interface CustomerDetail {
  customerId: number;
  managerId: number;
  customerName: string;
  customerPin: number;
  enabled: boolean;
  billable: boolean;
  debtorId: number | null;
  debtorName: string;
  /** Read-only feature flag: the customer has the IPPBX (shows the PBX settings tile). */
  pbxEnabled: boolean;
}

/** IPPBX settings of a customer (CustomerSetting 'PbxInternalCalls', script 20). */
export interface CustomerPbxSettings {
  /** Extensions of the customer may dial each other's 4-digit extension and transfer IVR calls to each other. */
  internalCalls: boolean;
}

export interface Option {
  id: number;
  name: string;
}

export interface ApiKey {
  apiKeyCustomerId: number;
  apiKey: string;
}

export interface MiscCharge {
  customerMiscChargesId: number;
  description: string;
  nrc: number;
  mrc: number;
  validFromUtc: string;
  validTillUtc: string;
}

export interface InteractionPricing {
  interactionPricingId: number;
  siteId: number | null;
  siteName: string;
  perSession: number;
  perMinute: number;
  validFromUtc: string;
  validTillUtc: string;
  isActive: boolean;
}

export interface Login {
  loginId: number;
  username: string;
}

export interface LoginGroup {
  portalPermissionGroupId: number;
  name: string;
  assigned: boolean;
}

export interface CustomerTimeZone {
  /** Stored sys.time_zone_info name; null = UTC. */
  timeZone: string | null;
  timeZoneIana: string;
}

export interface TimeZoneOption {
  id: string;
  label: string;
  offsetMinutes: number;
}

/** welcome = account created + set-password link; username = reminder; reset = password-reset link. */
export type LoginMailKind = "welcome" | "username" | "reset";

/** One typed customer setting (dbo.CustomerSetting + dbo.CustomerSettingDefinition, script 25). */
export interface CustomerSettingItem {
  settingName: string;
  displayName: string;
  description: string | null;
  category: string;
  valueType: "bool" | "int" | "decimal" | "text" | "email" | "select" | "timezone" | string;
  options: { value: string; label: string }[];
  minValue: number | null;
  maxValue: number | null;
  /** Managers may edit this setting for their own customers. */
  managerEditable: boolean;
  /** A definition row exists; false = only known from dbo.CustomerSetting (shown as text). */
  defined: boolean;
  /** Effective value: the customer's override, else the global default; null when neither exists. */
  value: string | null;
  defaultValue: string | null;
  isCustomerSpecific: boolean;
}

export interface CustomerSettingsResponse {
  items: CustomerSettingItem[];
  /** dbo.CustomerSettingDefinition exists (script 25 ran). */
  definitionsAvailable: boolean;
  /** The caller may flag settings as manager-editable (admins). */
  canFlagManager: boolean;
}

export const customersAdminApi = {
  listSettings: (customerId: number) => http<CustomerSettingsResponse>(`/api/admin/customers/${customerId}/settings`),
  setSetting: (customerId: number, settingName: string, value: string) =>
    http<CustomerSettingItem>(`/api/admin/customers/${customerId}/settings/${encodeURIComponent(settingName)}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),
  resetSetting: (customerId: number, settingName: string) =>
    http<CustomerSettingItem>(`/api/admin/customers/${customerId}/settings/${encodeURIComponent(settingName)}`, { method: "DELETE" }),
  setSettingManagerEditable: (settingName: string, managerEditable: boolean) =>
    http<{ settingName: string; managerEditable: boolean }>(`/api/admin/customers/settings-definitions/${encodeURIComponent(settingName)}/manager-editable`, {
      method: "PUT",
      body: JSON.stringify({ managerEditable }),
    }),

  list: () => http<AdminCustomerListResponse>("/api/admin/customers"),

  sendLoginMail: (customerId: number, loginId: number, kind: LoginMailKind) =>
    http<{ sentTo: string; kind: LoginMailKind }>(`/api/admin/customers/${customerId}/logins/${loginId}/mail/${kind}`, { method: "POST" }),

  getPbxSettings: (customerId: number) => http<CustomerPbxSettings>(`/api/admin/customers/${customerId}/pbx-settings`),
  setPbxSettings: (customerId: number, body: CustomerPbxSettings) =>
    http<CustomerPbxSettings>(`/api/admin/customers/${customerId}/pbx-settings`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  timezoneOptions: () => http<TimeZoneOption[]>("/api/admin/customers/options/timezones"),
  getTimeZone: (customerId: number) => http<CustomerTimeZone>(`/api/admin/customers/${customerId}/timezone`),
  setTimeZone: (customerId: number, timeZone: string | null) =>
    http<CustomerTimeZone>(`/api/admin/customers/${customerId}/timezone`, {
      method: "PUT",
      body: JSON.stringify({ timeZone }),
    }),

  /**
   * Impersonates the given customer by re-authenticating the current
   * session with a single-use validation key issued by
   * dbo.obj_CustomerLogin_GetByCustomerId. Returns the new
   * (Customer-kind) session.
   */
  switchTo: (customerId: number) =>
    http<SessionDto>(`/api/admin/customers/${customerId}/switch`, {
      method: "POST",
    }),

  /**
   * Restores the admin identity previously stashed on the session
   * by switchTo. No-op when not impersonating.
   */
  exitImpersonate: () =>
    http<SessionDto>("/api/admin/customers/exit-impersonate", {
      method: "POST",
    }),

  // ---- customer management (shared admin + manager) ----

  getDetail: (customerId: number) =>
    http<CustomerDetail>(`/api/admin/customers/${customerId}`),

  create: (name: string, managerId?: number) =>
    http<{ customerId: number }>("/api/admin/customers", {
      method: "POST",
      body: JSON.stringify({ name, managerId: managerId ?? null }),
    }),

  update: (
    customerId: number,
    body: {
      customerName: string;
      enabled: boolean;
      billable: boolean;
      debtorId: number | null;
      managerId?: number | null;
    },
  ) =>
    http<CustomerDetail>(`/api/admin/customers/${customerId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  generatePin: (customerId: number) =>
    http<{ pin: string | null }>(`/api/admin/customers/${customerId}/pincode`, {
      method: "POST",
    }),

  managerOptions: () =>
    http<Option[]>("/api/admin/customers/options/managers"),

  debtorOptions: () => http<Option[]>("/api/admin/customers/options/debtors"),

  apiKeys: (customerId: number) =>
    http<ApiKey[]>(`/api/admin/customers/${customerId}/apikeys`),

  createApiKey: (customerId: number) =>
    http<ApiKey[]>(`/api/admin/customers/${customerId}/apikeys`, {
      method: "POST",
    }),

  deleteApiKey: (customerId: number, apiKeyCustomerId: number) =>
    http<ApiKey[]>(
      `/api/admin/customers/${customerId}/apikeys/${apiKeyCustomerId}`,
      { method: "DELETE" },
    ),

  miscCharges: (customerId: number) =>
    http<MiscCharge[]>(`/api/admin/customers/${customerId}/misc-charges`),

  upsertMiscCharge: (
    customerId: number,
    body: {
      customerMiscChargesId: number;
      description: string;
      nrc: number;
      mrc: number;
      validFromUtc: string;
      validTillUtc: string;
    },
  ) =>
    http<MiscCharge[]>(`/api/admin/customers/${customerId}/misc-charges`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deleteMiscCharge: (customerId: number, miscChargeId: number) =>
    http<MiscCharge[]>(
      `/api/admin/customers/${customerId}/misc-charges/${miscChargeId}`,
      { method: "DELETE" },
    ),

  interactionPricing: (customerId: number) =>
    http<InteractionPricing[]>(
      `/api/admin/customers/${customerId}/interaction-pricing`,
    ),

  upsertInteractionPricing: (
    customerId: number,
    body: {
      interactionPricingId: number;
      siteId: number;
      perSession: number;
      perMinute: number;
      validFromUtc: string;
      validTillUtc: string;
    },
  ) =>
    http<InteractionPricing[]>(
      `/api/admin/customers/${customerId}/interaction-pricing`,
      { method: "PUT", body: JSON.stringify(body) },
    ),

  // ---- logins (shared admin + manager) ----

  logins: (customerId: number) =>
    http<Login[]>(`/api/admin/customers/${customerId}/logins`),

  upsertLogin: (
    customerId: number,
    body: { loginId: number; username: string; password: string },
  ) =>
    http<Login[]>(`/api/admin/customers/${customerId}/logins`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  deleteLogin: (customerId: number, loginId: number) =>
    http<Login[]>(`/api/admin/customers/${customerId}/logins/${loginId}`, {
      method: "DELETE",
    }),

  loginGroups: (customerId: number, loginId: number) =>
    http<LoginGroup[]>(
      `/api/admin/customers/${customerId}/logins/${loginId}/groups`,
    ),

  addLoginGroup: (customerId: number, loginId: number, groupId: number) =>
    http<LoginGroup[]>(
      `/api/admin/customers/${customerId}/logins/${loginId}/groups/${groupId}`,
      { method: "POST" },
    ),

  removeLoginGroup: (customerId: number, loginId: number, groupId: number) =>
    http<LoginGroup[]>(
      `/api/admin/customers/${customerId}/logins/${loginId}/groups/${groupId}`,
      { method: "DELETE" },
    ),
};
